import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Badge,
  Alert,
  AlertDescription,
  Progress,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui';
import {
  DollarSign,
  Download,
  CreditCard,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Package,
  Truck,
  Upload,
  FileText,
  RefreshCw,
  Eye
} from 'lucide-react';
import { saveAs } from 'file-saver';
import { PDFDocument, StandardFonts, rgb, degrees } from 'pdf-lib';
import { fetchUpsMergedLabels } from '../MergeUPSLabels';

const API_URL = import.meta.env.VITE_API_URL;

const PurchaseLabelsSection = ({ 
  boxes, 
  costEstimate, 
  trackingCodeMap, 
  validationStatus, 
  selectedPlan,
  clientsData,
  asinData,
  shipmentInfo,
  shipmentLabels,     // Add shipmentLabels for real address data
  confirmedAddresses  // Add confirmed addresses from step 2
}) => {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientName, setClientName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [labels, setLabels] = useState([]);
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [importInfo, setImportInfo] = useState({});
  const [adjustedAddresses, setAdjustedAddresses] = useState({});
  const [upsFile, setUpsFile] = useState(null);
  const [stallionFile, setStallionFile] = useState(null);
  const [fdaPnFile, setFdaPnFile] = useState(null);
  const [upsFileName, setUpsFileName] = useState('');
  const [stallionFileName, setStallionFileName] = useState('');
  const [fdaPnFileName, setFdaPnFileName] = useState('');
  const [dragOverUps, setDragOverUps] = useState(false);
  const [dragOverStallion, setDragOverStallion] = useState(false);
  const [dragOverFdaPn, setDragOverFdaPn] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingPayloads, setPendingPayloads] = useState([]);
  const [purchaseProgress, setPurchaseProgress] = useState({ current: 0, total: 0 });
  const [upsLabelsLoading, setUpsLabelsLoading] = useState(false);

  // Load initial data
  useEffect(() => {
    console.log('PurchaseLabelsSection initial mount, boxes available:', boxes.length);
    fetchAdjustedAddresses();
  }, []);

  // Fetch import info when boxes change
  useEffect(() => {
    if (boxes.length > 0) {
      console.log('Boxes changed, fetching import info for', boxes.length, 'boxes');
      fetchImportInfo();
    }
  }, [boxes]);

  // Initialize client and fetch API key when data is available (matching PurchaseLabels.jsx pattern)
  useEffect(() => {
    if (shipmentInfo && clientsData && clientsData.length > 0) {
      // Find the client based on the team_id from shipmentInfo (same as PurchaseLabels.jsx)
      const client = clientsData.find(
        (client) => client.id === shipmentInfo.team_id
      );
      
      if (client && client.name !== clientName) {
        console.log("Found client for team_id:", shipmentInfo.team_id, "->", client.name);
        setClientName(client.name);
        fetchApiKey(client.name);
      } else if (!client) {
        console.log("No client found for team_id:", shipmentInfo.team_id);
        setError("No client found for this shipment.");
      }
    } else {
      console.log("shipmentInfo or clientsData not available yet");
    }
  }, [shipmentInfo, clientsData, clientName]);

  // Debug tracking codes (remove console logs for production)
  // useEffect(() => {
  //   console.log('PurchaseLabelsSection - Debug Data:');
  //   console.log('boxes:', boxes);
  //   console.log('trackingCodeMap:', trackingCodeMap);
  //   console.log('costEstimate:', costEstimate);
  // }, [boxes, trackingCodeMap, costEstimate]);

  // Update required credits when boxes change
  useEffect(() => {
    if (boxes.length > 0) {
      calculateRequiredCredits(boxes);
    }
  }, [boxes]);

  // Auto-prefill purchase amount with needed credits (minimum $10)
  useEffect(() => {
    if (credits !== null && costEstimate > 0 && credits < costEstimate) {
      const shortfall = costEstimate - credits;
      const amountToPrefill = Math.max(shortfall, 10); // Ensure minimum $10
      setPurchaseAmount(amountToPrefill.toFixed(2));
    }
  }, [credits, costEstimate]);

  // Debug ASIN data availability
  useEffect(() => {
    console.log('PurchaseLabelsSection - Debug Data:', {
      asinDataLength: asinData?.length || 0,
      asinDataSample: asinData?.slice(0, 2),
      boxesLength: boxes?.length || 0,
      firstBoxItems: boxes?.[0]?.items
    });
  }, [asinData, boxes]);

  // Fetch Stallion credits
  const fetchCredits = async (apiKey) => {
    if (!apiKey) return;
    
    console.log('Fetching credits with API key:', apiKey.substring(0, 10) + '...');
    
    try {
      const creditsResponse = await fetch(
        "https://ship.stallionexpress.ca/api/v3/credits",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${apiKey}`,
          },
        }
      );
      
      console.log('Credits Response Status:', creditsResponse.status);
      
      if (creditsResponse.ok) {
        const creditsData = await creditsResponse.json();
        console.log('Credits Data:', creditsData);
        setCredits(parseFloat(creditsData.credits));
      } else {
        const errorText = await creditsResponse.text();
        console.log('Credits Error Response:', errorText);
        setError(`Failed to fetch credits: ${creditsResponse.status} ${creditsResponse.statusText}`);
      }
    } catch (error) {
      console.error("Error fetching credits:", error);
      setError("Failed to fetch credits. Please try again.");
    }
  };

  // Fetch API key for client
  const fetchApiKey = async (clientNameParam) => {
    const nameToUse = clientNameParam || clientName;
    if (!nameToUse) return;
    
    console.log('Fetching API key for client:', nameToUse);
    
    try {
      const response = await fetch(`${API_URL}/api/getStallionApiKey`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ clientName: nameToUse }),
      });
      const data = await response.json();

      console.log('API Key Response:', data);

      if (data.apiKey) {
        console.log('API Key fetched successfully:', data.apiKey.substring(0, 10) + '...');
        setApiKey(data.apiKey);
        fetchCredits(data.apiKey);
        setError("");
      } else {
        console.log('No API key found in response');
        setError("API key not found for client: " + nameToUse);
      }
    } catch (error) {
      console.error('Error fetching API key:', error);
      setError("Failed to fetch API key. Please try again.");
    }
  };

  // Fetch import info for all ASINs (using batch endpoint like the backend)
  const fetchImportInfo = async () => {
    if (!boxes.length) {
      console.log('No boxes available for import info fetch');
      return;
    }

    const asins = [...new Set(boxes.flatMap(box => 
      box.items?.map(item => item.asin) || []
    ))].filter(asin => asin && asin.trim());

    console.log('Fetching import info for ASINs:', asins);
    
    if (asins.length === 0) {
      console.log('No valid ASINs found, skipping import info fetch');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/usa-import-info/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ asins }),
      });

      if (!response.ok) {
        console.error('Failed to fetch batch import info:', response.statusText);
        return;
      }

      const importInfoMap = await response.json();
      
             // Convert the response format to match what we expect
       const formattedImportInfo = {};
       Object.keys(importInfoMap).forEach(asin => {
         formattedImportInfo[asin] = {
           countryOfOrigin: importInfoMap[asin]?.country_of_origin || '',
           htsCode: importInfoMap[asin]?.hts_code || '',
           photos: importInfoMap[asin]?.origin_photos || []
         };
       });
      
      setImportInfo(formattedImportInfo);
    } catch (error) {
      console.error('Error fetching import info:', error);
    }
  };

  // Fetch adjusted addresses
  const fetchAdjustedAddresses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/warehouse-addresses`);
      const data = await response.json();
      const adjustedAddressesMap = {};
      data.forEach(address => {
        adjustedAddressesMap[address.warehouse_id] = address;
      });
      setAdjustedAddresses(adjustedAddressesMap);
    } catch (error) {
      console.error('Error fetching adjusted addresses:', error);
    }
  };

  // Calculate required credits
  const calculateRequiredCredits = (boxes) => {
    let total = 0;
    boxes.forEach((box) => {
      const weightLbs = box.weight * 2.20462;
      let cost = 0;

      if (weightLbs < 10) cost = 3.99;
      else if (weightLbs >= 10 && weightLbs < 20) cost = 5.99;
      else if (weightLbs >= 20 && weightLbs < 40) cost = 9.99;
      else cost = 13.99;

      total += cost;
    });
    return total;
  };

  // Get price for ASIN
  const getPrice = (asin) => {
    if (!asinData || !Array.isArray(asinData)) {
      console.warn('asinData not loaded properly:', asinData);
      return 0;
    }
    
    const data = asinData.find((item) => item.ASIN === asin);
    const price = data ? parseFloat(data.VALUE) : 0;
    
    if (price === 0) {
      console.warn(`No price found for ASIN: ${asin}`, data);
    }
    
    return price;
  };

  // Calculate box value
  const calculateBoxValue = (box) => {
    if (!box.items || !Array.isArray(box.items)) {
      console.warn(`Box ${box.box_number} has no items:`, box);
      return 0;
    }
    
    const totalValue = box.items.reduce((acc, item) => {
      const price = getPrice(item.asin);
      const quantity = item.QTY || item.quantity || 1;
      const itemTotal = price * quantity;
      
      console.log(`Box ${box.box_number} - ASIN ${item.asin}: $${price} × ${quantity} = $${itemTotal}`);
      
      return acc + itemTotal;
    }, 0);
    
    console.log(`Box ${box.box_number} total value: $${totalValue}`);
    return totalValue;
  };

  // Get the validated address for a box (either from adjusted addresses or shipment data)
  const getValidatedAddressForBox = (boxNumber) => {
    // Find which shipment this box belongs to
    const confirmedPlacement = selectedPlan?.placement_options?.find(option => option.confirmed_at);
    if (!confirmedPlacement) return null;

    for (const shipment of confirmedPlacement.shipments) {
      const boxBelongsToShipment = shipment.boxes?.some(b => b.box_number === parseInt(boxNumber));
      if (boxBelongsToShipment) {
        const warehouseId = shipment.destination?.warehouse_id;
        
        // Check if we have an adjusted address from step 2 validation
        const adjustedAddress = adjustedAddresses[warehouseId];
        if (adjustedAddress) {
          return {
            address_line_1: adjustedAddress.address_line_1,
            address_line_2: adjustedAddress.address_line_2 || "",
            city: adjustedAddress.city,
            state_or_province_code: adjustedAddress.state_or_province_code,
            postal_code: adjustedAddress.postal_code,
            country_code: "US" // USA shipments are always to US
          };
        }
        
        // Fall back to original shipment destination address
        const originalAddress = shipment.destination?.address;
        if (originalAddress) {
          return {
            address_line_1: originalAddress.address_line_1 || '',
            address_line_2: originalAddress.address_line_2 || "",
            city: originalAddress.city || '',
            state_or_province_code: originalAddress.state_or_province_code || '',
            postal_code: originalAddress.postal_code || '',
            country_code: originalAddress.country_code || "US"
          };
        }
      }
    }
    
    return null;
  };

  // Handle label purchase
  const handlePurchaseLabels = async () => {
    if (!validationStatus.readyToPurchase) {
      alert('Please complete all validation steps first');
      return;
    }

    // Fix floating-point comparison issues by adding a small tolerance
    // Allow purchase when credits are equal to or greater than cost (with tolerance for floating point)
    if ((credits || 0) + 0.01 < costEstimate) {
      alert(`Insufficient credits. Required: $${costEstimate.toFixed(2)}, Available: $${(credits || 0).toFixed(2)}`);
      return;
    }

    // Build payloads for confirmation
    const payloads = [];
    const confirmedPlacement = selectedPlan.placement_options.find(option => option.confirmed_at);
    
    console.log('Building payloads for', boxes.length, 'boxes');
    console.log('Confirmed placement found:', !!confirmedPlacement);
    console.log('Raw boxes array:', boxes);
    
    for (const box of boxes) {
      console.log(`Processing box ${box.box_number}...`);
      console.log('Full box object:', box);
      
      const trackingCode = trackingCodeMap[box.box_number];
      const boxValue = calculateBoxValue(box);
      
      console.log(`Box ${box.box_number}: trackingCode=${trackingCode}, value=${boxValue}`);
      
      // Get the validated address for this box (from step 2 validation)
      const validatedAddress = getValidatedAddressForBox(box.box_number);
      
      console.log(`Box ${box.box_number}: validatedAddress=`, validatedAddress);
      
      if (!validatedAddress) {
        console.error(`No validated address found for box ${box.box_number}`);
        continue; // Skip this box if no address found
      }

      const addressToUse = {
        address1: validatedAddress.address_line_1,
        address2: validatedAddress.address_line_2,
        city: validatedAddress.city,
        province_code: validatedAddress.state_or_province_code,
        postal_code: validatedAddress.postal_code,
        country_code: validatedAddress.country_code
      };

      // Build proper Stallion API payload (matching the original backend implementation)
      const payload = {
        name: clientName.substring(0, 40),
        address1: addressToUse.address1,
        address2: addressToUse.address2,
        city: addressToUse.city,
        province_code: addressToUse.province_code,
        postal_code: addressToUse.postal_code,
        country_code: addressToUse.country_code,
        weight_unit: "kg",
        weight: parseFloat(box.weight),
        length: parseFloat(box.length),
        width: parseFloat(box.width),
        height: parseFloat(box.height),
        size_unit: "cm",
        items: box.items?.map((item) => ({
          currency: "USD",
          description: (item.TITLE || item.title || `ASIN: ${item.asin}`).slice(0, 40),
          quantity: item.QTY || item.quantity || 1,
          sku: item.asin,
          value: getPrice(item.asin),
          country_of_origin: importInfo[item.asin]?.countryOfOrigin || ''
        })) || [],
        value: boxValue,
        currency: "USD",
        package_type: "parcel",
        postage_type: "ups_standard",
        signature_confirmation: false,
        label_format: "png",
        purchase_label: true,
        is_fba: true,
        tracking_code: trackingCode
      };

      payloads.push({
        boxNumber: box.box_number,
        payload: payload
      });
    }

    // Show confirmation dialog
    console.log('Final payloads array:', payloads);
    console.log('Number of payloads:', payloads.length);
    
    if (payloads.length === 0) {
      setError('No valid payloads could be generated. Please check that addresses are validated and tracking codes are available.');
      return;
    }
    
    setPendingPayloads(payloads);
    setShowConfirmDialog(true);
  };

  // Execute the actual purchase after confirmation
  const executePurchase = async () => {
    setShowConfirmDialog(false);
    setPurchaseLoading(true);
    setPurchaseProgress({ current: 0, total: pendingPayloads.length });
    setError('');
    
    try {
      const labelsData = [];
      
      for (const [index, { boxNumber, payload }] of pendingPayloads.entries()) {
        console.log(`Submitting payload for Box ${boxNumber}:`, JSON.stringify(payload, null, 2));

        // Submit to Stallion API
        const response = await fetch("https://ship.stallionexpress.ca/api/v3/shipments", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Stallion API Error for Box ${boxNumber}:`, errorText);
          throw new Error(`Failed to create label for box ${boxNumber}: ${response.status} ${response.statusText}`);
        }

        const labelData = await response.json();
        console.log(`Stallion API Response for Box ${boxNumber}:`, labelData);
        
        labelsData.push({
          boxNumber: boxNumber,
          trackingCode: payload.tracking_code,
          stallionId: labelData.id,
          labelUrl: labelData.label_url,
          ...labelData
        });

        // Update progress
        setPurchaseProgress({ current: index + 1, total: pendingPayloads.length });
      }

      setLabels(labelsData);
      
      // Download combined PDF
      await downloadCombinedLabels(labelsData);
      
      setPurchaseComplete(true);
      
    } catch (error) {
      console.error("Error purchasing labels:", error);
      setError(error.message || "Failed to purchase labels. Please try again.");
    } finally {
      setPurchaseLoading(false);
      fetchCredits(apiKey); // Refresh credits
    }
  };

  // Download combined labels as PDF
  const downloadCombinedLabels = async (labelsData) => {
    try {
      const pdfDoc = await PDFDocument.create();
      
      for (const label of labelsData) {
        if (label.labelUrl) {
          const response = await fetch(label.labelUrl);
          const pdfBytes = await response.arrayBuffer();
          const labelPdf = await PDFDocument.load(pdfBytes);
          const pages = await pdfDoc.copyPages(labelPdf, labelPdf.getPageIndices());
          pages.forEach((page) => pdfDoc.addPage(page));
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      saveAs(blob, `STALLION_${selectedPlan.inbound_plan_id || selectedPlan.id}.pdf`);
      
    } catch (error) {
      console.error('Error creating combined PDF:', error);
    }
  };

  // Purchase credits
  const handlePurchaseCredits = async () => {
    if (!purchaseAmount || parseFloat(purchaseAmount) < 10) {
      setError("Minimum purchase amount is $10.");
      return;
    }

    if (!apiKey) {
      setError("API key not available. Please refresh the page.");
      return;
    }

    console.log('Purchase Credits Debug:');
    console.log('Client Name:', clientName);
    console.log('API Key:', apiKey ? `${apiKey.substring(0, 10)}...` : 'MISSING');
    console.log('Purchase Amount:', purchaseAmount);

    setPurchaseLoading(true);
    try {
      const response = await fetch(
        "https://ship.stallionexpress.ca/api/v3/credits",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            amount: parseFloat(purchaseAmount),
          }),
        }
      );

      console.log('Purchase Response Status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Purchase Success:', result);
        
        // Re-fetch credits to get the updated balance instead of relying on purchase response
        await fetchCredits(apiKey);
        
        setPurchaseAmount("");
        setError("");
      } else {
        const errorText = await response.text();
        console.log('Purchase Error Response:', errorText);
        setError(`Failed to purchase credits: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error purchasing credits:", error);
      setError("Failed to purchase credits. Please try again.");
    } finally {
      setPurchaseLoading(false);
    }
  };

  // Handle file uploads
  const handleFileUpload = (file, type) => {
    if (file && file.type === 'application/pdf') {
      if (type === 'ups') {
        setUpsFile(file);
        setUpsFileName(file.name);
      } else if (type === 'stallion') {
        setStallionFile(file);
        setStallionFileName(file.name);
      } else if (type === 'fdapn') {
        setFdaPnFile(file);
        setFdaPnFileName(file.name);
      }
    } else {
      alert('Please upload a PDF file');
    }
  };

  // Handle drag and drop
  const handleDragOver = (e, type) => {
    e.preventDefault();
    if (type === 'ups') setDragOverUps(true);
    else if (type === 'stallion') setDragOverStallion(true);
    else if (type === 'fdapn') setDragOverFdaPn(true);
  };

  const handleDragLeave = (e, type) => {
    e.preventDefault();
    if (type === 'ups') setDragOverUps(false);
    else if (type === 'stallion') setDragOverStallion(false);
    else if (type === 'fdapn') setDragOverFdaPn(false);
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    if (type === 'ups') setDragOverUps(false);
    else if (type === 'stallion') setDragOverStallion(false);
    else if (type === 'fdapn') setDragOverFdaPn(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0], type);
    }
  };

  // Handle file uploads and merging
  const handleMergeLabels = async () => {
    if (!upsFile || !stallionFile) {
      alert("Please upload both UPS and Stallion label PDFs first.");
      return;
    }

    setMergeLoading(true);

    try {
      const mergedPdf = await PDFDocument.create();

      // Read and copy pages from all PDFs
      const upsPdfBytes = await upsFile.arrayBuffer();
      const stallionPdfBytes = await stallionFile.arrayBuffer();
      
      const upsPdf = await PDFDocument.load(upsPdfBytes);
      const stallionPdf = await PDFDocument.load(stallionPdfBytes);
      
      const upsPages = await mergedPdf.copyPages(upsPdf, upsPdf.getPageIndices());
      const stallionPages = await mergedPdf.copyPages(stallionPdf, stallionPdf.getPageIndices());

      // Handle FDA PN file if provided
      let fdaPnPages = [];
      if (fdaPnFile) {
        const fdaPnPdfBytes = await fdaPnFile.arrayBuffer();
        const fdaPnPdf = await PDFDocument.load(fdaPnPdfBytes);
        fdaPnPages = await mergedPdf.copyPages(fdaPnPdf, fdaPnPdf.getPageIndices());
      }

      const helveticaBoldFont = await mergedPdf.embedFont(StandardFonts.HelveticaBold);
      const totalBoxes = stallionPages.length;

      let upsIndex = 0;
      let stallionIndex = 0;
      let fdaPnIndex = 0;

      while (upsIndex < upsPages.length || stallionIndex < stallionPages.length) {
        // Add up to 2 UPS pages (unstamped)
        for (let i = 0; i < 2 && upsIndex < upsPages.length; i++) {
          mergedPdf.addPage(upsPages[upsIndex]);
          upsIndex++;
        }

        // Add 1 Stallion page and stamp it
        if (stallionIndex < stallionPages.length) {
          const page = stallionPages[stallionIndex];
          const currentBoxNumber = stallionIndex + 1;
          const stampText = `B${currentBoxNumber}o${totalBoxes}`;
          
          page.drawText(stampText, {
            x: 126.00,
            y: 328.00,
            size: 12,
            font: helveticaBoldFont,
            color: rgb(0, 0, 0),
            rotate: degrees(0), // Set to 0 for horizontal, left-to-right text
          });

          mergedPdf.addPage(page);
          stallionIndex++;
        }

        // Add 1 FDA PN page if available (unstamped)
        if (fdaPnPages.length > 0 && fdaPnIndex < fdaPnPages.length) {
          mergedPdf.addPage(fdaPnPages[fdaPnIndex]);
          fdaPnIndex++;
        }
      }

      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
      const fileName = fdaPnFile 
        ? `MERGED_${selectedPlan.inbound_plan_id || selectedPlan.id}.pdf`
        : `MERGED_${selectedPlan.inbound_plan_id || selectedPlan.id}.pdf`;
      saveAs(blob, fileName);
      
    } catch (error) {
      console.error('Error merging labels:', error);
      setError('Failed to merge labels. Please check the PDF files.');
    } finally {
      setMergeLoading(false);
    }
  };

  const handleRedownloadLabels = async () => {
    if (labels.length === 0) {
      setError('No labels available to download');
      return;
    }

    try {
      await downloadCombinedLabels(labels);
    } catch (error) {
      console.error('Error redownloading labels:', error);
      setError('Failed to redownload labels');
    }
  };

  // Fix floating-point comparison issues by adding a small tolerance
  // Consider credits sufficient if they're equal to or greater than cost (with tolerance)
  const hasEnoughCredits = (credits || 0) + 0.01 >= costEstimate;

  // Download UPS Labels (using same logic as Shipment.jsx)
  const downloadUPSLabels = () => {
    if (!selectedPlan || !selectedPlan.placement_options) {
      setError('No plan selected or placement options available');
      return;
    }

    setUpsLabelsLoading(true);
    setError('');

    // Find the earliest confirmed placement option (same logic as Shipment.jsx)
    const earliestConfirmedOption = selectedPlan.placement_options.reduce(
      (earliest, option) => {
        if (
          option.confirmed_at &&
          (!earliest || new Date(option.confirmed_at) < new Date(earliest.confirmed_at))
        ) {
          return option;
        }
        return earliest;
      },
      null
    );

    if (earliestConfirmedOption) {
      fetchUpsMergedLabels(
        earliestConfirmedOption, 
        selectedPlan.id, 
        (progress) => {
          console.log('UPS Labels Progress:', progress);
          // You could show progress in UI here if needed
        }
      ).then(() => {
        setUpsLabelsLoading(false);
      }).catch((error) => {
        console.error('Error downloading UPS labels:', error);
        setError('Failed to download UPS labels: ' + error.message);
        setUpsLabelsLoading(false);
      });
    } else {
      setError('No confirmed placement option found for UPS labels');
      setUpsLabelsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Compact Header with Credits */}
      <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded">
        <div className="flex items-center space-x-3">
          <DollarSign className="w-5 h-5 text-green-600" />
          <div>
            <h3 className="text-base font-medium">Purchase Shipping Labels</h3>
            <p className="text-xs text-slate-600">Generate labels with tracking integration</p>
          </div>
        </div>
        
        {purchaseComplete && (
          <Badge variant="default" className="bg-green-100 text-green-700 text-xs">
            <CheckCircle className="w-3 h-3 mr-1" />
            Complete
          </Badge>
        )}
      </div>

      {/* Condensed Box Table */}
      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
          <h4 className="text-sm font-medium text-slate-700">Boxes ({boxes.length})</h4>
        </div>
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="border-b border-slate-200">
                <th className="text-left p-2 font-medium text-slate-700">Box</th>
                <th className="text-left p-2 font-medium text-slate-700">UPS Tracking</th>
                <th className="text-right p-2 font-medium text-slate-700">Cost</th>
              </tr>
            </thead>
                         <tbody className="divide-y divide-slate-100">
              {boxes.map((box) => {
                // Debug tracking codes
                const trackingCode = trackingCodeMap[box.box_number] || trackingCodeMap[parseInt(box.box_number)];
                const cost = calculateRequiredCredits([box]);
                const boxNum = box.box_number || 'N/A';

                return (
                  <tr key={boxNum} className="hover:bg-slate-50">
                    <td className="p-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded text-xs font-medium flex items-center justify-center">
                          {boxNum}
                        </div>
                        <div>
                          <div className="font-medium">Box {boxNum}</div>
                          <div className="text-slate-500">
                            {box.length}×{box.width}×{box.height} • {box.weight}kg
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-2">
                      <code className={`text-xs px-2 py-1 rounded font-mono ${
                        trackingCode ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {trackingCode || 'Not assigned'}
                      </code>
                    </td>
                    <td className="p-2 text-right">
                      <span className="font-semibold text-green-600">${cost.toFixed(2)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insufficient Credits Warning */}
      {!hasEnoughCredits && (
        <div className="bg-orange-50 border border-orange-200 rounded p-3">
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-800">Insufficient Credits</span>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              placeholder="Amount ($10 min)"
              value={purchaseAmount}
              onChange={(e) => setPurchaseAmount(e.target.value)}
              className="flex-1 px-2 py-1 text-sm border border-orange-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
              min="10"
              step="0.01"
            />
            <Button
              onClick={handlePurchaseCredits}
              disabled={parseFloat(purchaseAmount) < 10 || purchaseLoading}
              size="sm"
              className="bg-orange-600 hover:bg-orange-700 text-xs"
            >
              {purchaseLoading ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Buying...
                </>
              ) : (
                "Add Credits"
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Purchase Action */}
      <div className="bg-white border border-slate-200 rounded p-4">
        {purchaseComplete ? (
          <div className="text-center space-y-3">
            <p className="text-sm font-medium text-green-600 flex items-center justify-center space-x-1">
                <CheckCircle className="w-4 h-4" />
                <span>Labels generated successfully!</span>
            </p>
            <div className="flex items-center justify-center space-x-2">
              <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleRedownloadLabels}
              >
                  <Download className="w-3 h-3 mr-1" />
                  Redownload Labels
              </Button>
              <Button 
                  variant="outline"
                  size="sm"
                  onClick={downloadUPSLabels}
                  disabled={upsLabelsLoading}
              >
                  {upsLabelsLoading ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Truck className="w-3 h-3 mr-1" />
                      Download UPS Labels
                    </>
                  )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-6">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <span className="text-slate-600">Available:</span>
                  <span className="font-semibold text-blue-600">${credits?.toFixed(2) || '0.00'}</span>
                  <button
                    onClick={() => fetchCredits(apiKey)}
                    disabled={!apiKey}
                    className="ml-1 p-1 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                    title="Refresh credits"
                  >
                    <RefreshCw className="w-3 h-3 text-blue-600" />
                  </button>
                </div>
                <div className="flex items-center space-x-1">
                  <Package className="w-4 h-4 text-orange-600" />
                  <span className="text-slate-600">Required:</span>
                  <span className="font-semibold text-orange-600">${costEstimate.toFixed(2)}</span>
                </div>
                {!hasEnoughCredits && (
                  <div className="flex items-center space-x-1">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <span className="text-slate-600">Need:</span>
                    <span className="font-semibold text-red-600">${(costEstimate - (credits || 0)).toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              {!hasEnoughCredits && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
                  <div className="flex items-center justify-center space-x-2 text-red-700">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">Insufficient Credits - Add ${(costEstimate - (credits || 0)).toFixed(2)} to continue</span>
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-center space-x-2">
                <Button
                  onClick={handlePurchaseLabels}
                  disabled={!validationStatus.readyToPurchase || !hasEnoughCredits || purchaseLoading}
                  className={`font-medium ${
                    !hasEnoughCredits 
                      ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                  size="lg"
                >
                  {purchaseLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Purchase All Labels (${costEstimate.toFixed(2)})
                    </>
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={downloadUPSLabels}
                  disabled={upsLabelsLoading}
                  size="lg"
                >
                  {upsLabelsLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Truck className="w-4 h-4 mr-2" />
                      Download UPS Labels
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {purchaseLoading && (
              <div className="space-y-1">
                <Progress value={(purchaseProgress.current / purchaseProgress.total) * 100} className="h-1" />
                <p className="text-xs text-slate-500">
                  Purchasing label {purchaseProgress.current} of {purchaseProgress.total}...
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* File Upload & Merge Section */}
      <div className="bg-white border border-slate-200 rounded">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
          <h4 className="text-sm font-medium text-slate-700">Upload & Merge Labels</h4>
        </div>
        <div className="p-4 space-y-4">
          {/* File Upload Areas */}
          <div className="grid grid-cols-3 gap-3">
            {/* UPS File Upload */}
            <div
              className={`border-2 border-dashed rounded p-3 text-center transition-colors cursor-pointer ${
                dragOverUps ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
              }`}
              onDragOver={(e) => handleDragOver(e, 'ups')}
              onDragLeave={(e) => handleDragLeave(e, 'ups')}
              onDrop={(e) => handleDrop(e, 'ups')}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.pdf';
                input.onchange = (e) => handleFileUpload(e.target.files[0], 'ups');
                input.click();
              }}
            >
              <Upload className="w-5 h-5 text-slate-400 mx-auto mb-2" />
              <p className="text-xs font-medium text-slate-900 mb-1">UPS Labels</p>
              {upsFileName ? (
                <div className="text-xs text-green-600 font-medium">✓ {upsFileName}</div>
              ) : (
                <p className="text-xs text-slate-500">Click or drag PDF</p>
              )}
            </div>

            {/* Stallion File Upload */}
            <div
              className={`border-2 border-dashed rounded p-3 text-center transition-colors cursor-pointer ${
                dragOverStallion ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
              }`}
              onDragOver={(e) => handleDragOver(e, 'stallion')}
              onDragLeave={(e) => handleDragLeave(e, 'stallion')}
              onDrop={(e) => handleDrop(e, 'stallion')}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.pdf';
                input.onchange = (e) => handleFileUpload(e.target.files[0], 'stallion');
                input.click();
              }}
            >
              <Upload className="w-5 h-5 text-slate-400 mx-auto mb-2" />
              <p className="text-xs font-medium text-slate-900 mb-1">Stallion Labels</p>
              {stallionFileName ? (
                <div className="text-xs text-green-600 font-medium">✓ {stallionFileName}</div>
              ) : (
                <p className="text-xs text-slate-500">Click or drag PDF</p>
              )}
            </div>

            {/* FDA PN File Upload */}
            <div
              className={`border-2 border-dashed rounded p-3 text-center transition-colors cursor-pointer ${
                dragOverFdaPn ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
              }`}
              onDragOver={(e) => handleDragOver(e, 'fdapn')}
              onDragLeave={(e) => handleDragLeave(e, 'fdapn')}
              onDrop={(e) => handleDrop(e, 'fdapn')}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.pdf';
                input.onchange = (e) => handleFileUpload(e.target.files[0], 'fdapn');
                input.click();
              }}
            >
              <Upload className="w-5 h-5 text-slate-400 mx-auto mb-2" />
              <p className="text-xs font-medium text-slate-900 mb-1">FDA PN Labels</p>
              <p className="text-xs text-slate-400 mb-1">(Optional)</p>
              {fdaPnFileName ? (
                <div className="text-xs text-green-600 font-medium">✓ {fdaPnFileName}</div>
              ) : (
                <p className="text-xs text-slate-500">Click or drag PDF</p>
              )}
            </div>
          </div>

          {/* Merge Info & Button */}
          <div className="text-center space-y-2">
            <div className="text-xs text-slate-600">
              {fdaPnFile ? (
                <span className="text-green-700 font-medium">
                  Pattern: 2 UPS + 1 Stallion + 1 FDA PN per box
                </span>
              ) : (
                <span>
                  Pattern: 2 UPS + 1 Stallion per box
                  <span className="text-slate-400 ml-1">(add FDA PN for extended merge)</span>
                </span>
              )}
            </div>
            <Button
              onClick={handleMergeLabels}
              disabled={!upsFile || !stallionFile || mergeLoading}
              className="bg-purple-600 hover:bg-purple-700"
              size="sm"
            >
              {mergeLoading ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <FileText className="w-3 h-3 mr-1" />
                  {fdaPnFile ? 'Merge Labels + FDA PN' : 'Merge Labels'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Eye className="w-5 h-5" />
              <span>Confirm Label Purchase</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              You are about to purchase Stallion Express labels for {pendingPayloads.length} box{pendingPayloads.length !== 1 ? 'es' : ''}:
            </div>
            
            {/* Box Summary */}
            <div className="bg-slate-50 border border-slate-200 rounded p-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="font-medium text-slate-700">Boxes:</span> {pendingPayloads.length}
                </div>
                <div>
                  <span className="font-medium text-slate-700">Total Cost:</span> ${costEstimate.toFixed(2)}
                </div>
              </div>
              {pendingPayloads.length > 0 && (
                <div className="mt-2 text-xs text-slate-600">
                  Box Numbers: {pendingPayloads.map(p => p.boxNumber).join(', ')}
                </div>
              )}
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <div className="flex items-center space-x-2 text-blue-700">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Important</span>
              </div>
              <ul className="text-xs text-blue-600 mt-2 space-y-1 list-disc list-inside">
                <li>This will charge ${costEstimate.toFixed(2)} from your Stallion credits</li>
                <li>Labels will be generated and downloaded automatically</li>
                <li>Tracking codes will be integrated with the labels</li>
                <li>This action cannot be undone</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={executePurchase}
              className="bg-green-600 hover:bg-green-700"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Confirm Purchase (${costEstimate.toFixed(2)})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseLabelsSection; 